/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Typography, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { RoutePath } from '../../shared/enums/RoutePath';
import { dataTestId } from '../../shared/utils/versionUtils';

const useStyles = makeStyles(theme => ({
  logo: {
    marginRight: '2rem',
    '&[data-focustype="key"]': {
      outline: 'none',
      boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
    },

    '&:focus': {
      outline: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      height: '3rem',
    },
    [theme.breakpoints.up('xl2')]: {
      marginLeft: ({ isNoMenuPage, showConfigurableHeader }) =>
        showConfigurableHeader ? 'unset' : isNoMenuPage ? '1.5rem' : '2.375rem',
    },
  },
  logoLink: {
    '&[data-focustype="key"]': {
      outline: 'none',
      boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
    },

    '&:focus': {
      outline: 'none',
    },
  },
  title: {
    flexGrow: 1,
    textTransform: 'uppercase',
    fontSize: theme?.typography?.title?.fontSize || 'small',
    borderLeft: `1px solid ${theme.palette.highlightsAndDividers}`,
    alignSelf: 'center',
    padding: '1rem',
    fontWeight: 700,
    lineHeight: '1.5rem',
  },
  logoHeight: {
    height: ({ showConfigurableHeader }) => (showConfigurableHeader ? '90px' : theme.logoHeight),
    [theme.breakpoints.down('sm')]: {
      height: ({ showConfigurableHeader }) => showConfigurableHeader && '60px',
    },
  },
}));

export default function Logo({ breakpointSmall, isNoMenuPage, showConfigurableHeader }) {
  const theme = useTheme();
  const location = useLocation();
  const classes = useStyles({ isNoMenuPage, showConfigurableHeader });
  const { t, i18n } = useTranslation();
  let logoPath = (breakpointSmall && theme.logoInnerMobile) || theme.logoInner;

  if (breakpointSmall && i18n.exists?.('logoInner.mobile.translated')) {
    logoPath = t('logoInner.mobile.translated');
  } else if (i18n.exists?.('logoInner.translated')) {
    logoPath = t('logoInner.translated');
  }

  const logoClassnames = clsx(classes.logo, classes.logoHeight);

  const linkProps = RoutePath.isUmaRoute(location?.pathname) ? {} : { to: '/' };

  return (
    <Box className={logoClassnames} display="flex">
      {RoutePath.isUmaRoute(location?.pathname) ? (
        <img src={logoPath} alt={t('homepage.logo.alt')} className={logoClassnames} />
      ) : (
        <Link
          {...linkProps}
          {...dataTestId({ value: 'goBackToHome' })}
          className={classes.logoLink}
          title={t('homepage.logo.title')}
        >
          <img src={logoPath} alt={t('homepage.logo.alt')} className={logoClassnames} />
        </Link>
      )}
      {!showConfigurableHeader && (
        <Typography variant="h6" component="span" className={classes.title}>
          {t('patent.e.filing')}
        </Typography>
      )}
    </Box>
  );
}
